import UserIcon from '../../assets/logo/UserIcon';
import classnames from 'classnames';
import useAuth from '../../hooks/global/useAuth';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import { useTranslation } from 'react-i18next';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import React, { useMemo } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import BackIcon from '../../assets/logo/BackIcon';
import useTheme from '../../hooks/ui/useTheme';

interface Props {
  className?: string;
}

export default function UserBadge(props: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Profile:UserBadge' });

  const { className } = props;

  const { profile } = useSelector((state: StoreInterface) => state.profile);

  const { isLoggedIn, logout } = useAuth();
  const { toggleLoginModal } = useAppMethods();
  const theme = useTheme();

  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const onClick = () => {
    if (!isLoggedIn) {
      return toggleLoginModal({
        openModal: true,
        onCloseRedirect: () => null,
      });
    }

    handleToggle();
  };

  async function logOut(event: Event | React.SyntheticEvent): Promise<void> {
    logout();

    handleClose(event);
  }

  const loginButton = useMemo(() => {
    if (!isLoggedIn) {
      return (
        <button
          onClick={onClick}
          className="flex items-center justify-center cursor-pointer border rounded-[16px] px-sm h-[36px]"
          ref={anchorRef}
          id="profile-button"
          aria-controls={open ? 'profile-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <p className="text-sm">{t('buttons.sign_in_sign_up')}</p>
        </button>
      );
    }

    return (
      <button
        onClick={onClick}
        className="flex text-sm justify-center gap-xs items-center border rounded-[16px] h-[36px] px-sm py-xs text-brand-text-secondary font-medium cursor-pointer"
        ref={anchorRef}
        id="profile-button"
        aria-controls={open ? 'profile-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <BackIcon className="h-3 !w-3 rotate-90" type={2} color={theme.color.brand.icon.color} />
        <UserIcon className="my-auto h-4 w-4" color={theme.color.brand.icon.color} fill={false} />
      </button>
    );
  }, [isLoggedIn, profile, t, theme]);

  return (
    <div className={classnames('flex relative', className)}>
      {loginButton}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
        className="z-50 shadow dark:shadow-none !mt-xs"
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="profile-menu"
                  aria-labelledby="profile-button"
                  onKeyDown={handleListKeyDown}
                  className="!px-xs"
                >
                  <MenuItem onClick={logOut}>Logout</MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
}
